import { DisplayMode, MediaMatcherService } from '@myre/ui-primitive-core';

import {
  computed,
  inject,
  Injectable,
  Injector,
  runInInjectionContext,
  signal,
  type Signal,
} from '@angular/core';
import { isNil } from 'lodash-es';

import { subscribeToMultiObservable } from '../../../helpers/signal.helpers';

import { simplifiedActionDropdownItemToConsolidated } from '../helpers';
import { type ConsolidatedSimplifiedActionDropdownItem } from '../interfaces';
import { RoutingService } from '../routing/routing.service';

import { displayModeDefaultOpenRecord } from './menu.constants';
import { routesToMenuBlocks } from './menu.helpers';
import { type ForcedDisplayMode, type MenuBlock } from './menu.interfaces';

@Injectable({ providedIn: 'root' })
export class MenuService {
  /**
   * Either the menu is opened or not.
   */
  public get opened(): Signal<boolean> {
    return this._opened;
  }

  /**
   * The menu blocks.
   * It's a list of items that will be displayed in the menu.
   * A border will be displayed between each block, whether it's a secondary or tertiary block.
   */
  public get menuBlocks(): Signal<MenuBlock[]> {
    return this._menuBlocks;
  }

  public get dropdownItems(): Signal<
    ConsolidatedSimplifiedActionDropdownItem[]
  > {
    return this._dropdownItems;
  }

  public get selectedDropdownItem(): Signal<number | string> {
    return this._selectedDropdownItem;
  }

  public handleItemClick(): void {
    this._closeMenuForSmallScreens();
  }

  public readonly _displayableMenu = computed(
    () => this._routingService.activatedRouteDataForBaseUrl()?.menu !== null,
  );

  private readonly _injector = inject(Injector);

  private readonly _mediaMatcherService = inject(MediaMatcherService);

  private readonly _routingService = inject(RoutingService);

  /**
   * This subject is used to force the menu to be opened or closed.
   * It controlled by the `toggle` method.
   * If the value is `null`, the menu will be opened or closed based on the display mode.
   * If the value is not `null`, the menu opened / closed state will persist until the display mode changes.
   */
  private readonly _forcedDisplayMode = signal<ForcedDisplayMode>(null);
  private readonly _currentDisplayMode =
    this._mediaMatcherService.currentDisplayMode;

  private _menuForCurrentRoute = computed(() =>
    typeof this._routingService.activatedRouteDataForBaseUrl()?.menu ===
    'function'
      ? runInInjectionContext(this._injector, () =>
          this._routingService.computeMenuPermissions$(
            this._routingService.activatedRouteDataForBaseUrl()?.menu(),
          ),
        )
      : null,
  );

  private _menu = subscribeToMultiObservable(this._menuForCurrentRoute);

  private _dropdownItems = computed(() =>
    (this._menu()?.dropdownItems || []).map(item =>
      simplifiedActionDropdownItemToConsolidated(item),
    ),
  );

  private readonly _selectedDropdownItem: Signal<number | string> = computed(
    () => this._menu()?.selectedDropdownItem,
  );

  private readonly _menuBlocks = computed<MenuBlock[]>(() => {
    return routesToMenuBlocks(
      this._menu()?.routes || [],
      this._routingService.baseUrl(),
    );
  });

  private readonly _opened = computed(() => {
    if (!this._displayableMenu()) {
      return false;
    }

    const currentDisplayMode = this._currentDisplayMode();
    const forcedDisplayMode = this._forcedDisplayMode();

    if (isNil(forcedDisplayMode)) {
      return displayModeDefaultOpenRecord[currentDisplayMode];
    }

    // We want the menu opened / closed state to persist until the display mode changes.
    if (currentDisplayMode === forcedDisplayMode.displayMode) {
      return forcedDisplayMode.open;
    }

    return displayModeDefaultOpenRecord[currentDisplayMode];
  });

  public toggle(): void {
    this._forcedDisplayMode.set({
      open: !this._opened(),
      displayMode: this._currentDisplayMode(),
    });
  }

  private _closeMenuForSmallScreens(): void {
    if (this._mediaMatcherService.currentDisplayMode() === DisplayMode.Small) {
      this._forcedDisplayMode.set({
        open: false,
        displayMode: this._currentDisplayMode(),
      });
    }
  }
}
